import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Plus, Trash2 } from "lucide-react";
import { api, errMsg, formatDate, rupiah } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { AdminHead, FilterSelect, Table, Td } from "@/components/admin/AdminUI";
import { Stars } from "@/components/Stars";
import { PageLoader } from "@/components/layout/RequireAuth";

const clean = (f) => Object.fromEntries(Object.entries(f).filter(([, v]) => v));

export function AdminTransactions() {
  const [f, setF] = useState({ purpose: "", status: "", date_from: "", date_to: "" });
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/admin/payments", { params: clean(f) }).then((r) => setRows(r.data)); }, [f]);
  const exportCsv = async () => {
    const res = await api.get("/admin/payments.csv", { params: clean(f), responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = "transaksi-tuntas.csv";
    a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div data-testid="admin-transactions">
      <AdminHead title="Transaksi" desc="Semua pembayaran (simulasi) untuk pesanan dan pembelian kredit.">
        <Button variant="outline" onClick={exportCsv} className="rounded-xl" data-testid="export-csv-button"><Download className="mr-1.5 h-4 w-4" /> Ekspor CSV</Button>
      </AdminHead>
      <div className="mb-4 flex flex-wrap gap-2">
        <FilterSelect value={f.purpose} onChange={(v) => setF({ ...f, purpose: v })} testId="tx-purpose-filter" options={[["", "Semua jenis"], ["order", "Pesanan"], ["credits", "Kredit"]]} />
        <FilterSelect value={f.status} onChange={(v) => setF({ ...f, status: v })} testId="tx-status-filter" options={[["", "Semua status"], ["paid", "Dibayar"], ["pending", "Menunggu"], ["kedaluwarsa", "Kedaluwarsa"], ["diganti", "Diganti"], ["dibatalkan", "Dibatalkan"], ["direfund", "Direfund (simulasi)"]]} />
        <Input type="date" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} className="h-10 w-40 rounded-xl" data-testid="tx-date-from" />
        <Input type="date" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} className="h-10 w-40 rounded-xl" data-testid="tx-date-to" />
      </div>
      {rows === null ? <PageLoader /> : (
        <Table cols={["Tanggal", "Keterangan", "Pengguna", "Metode", "Status", "Jumlah", "Komisi", "Bersih", "Pencairan"]} testId="transactions-table" rows={rows.map((p) => (
          <tr key={p.payment_id} data-testid={`tx-row-${p.payment_id}`}>
            <Td>{formatDate(p.created_at)}</Td><Td>{p.purpose === "credits" ? `Kredit: ${p.description}` : p.description}</Td><Td>{p.user_name}</Td>
            <Td>{p.method_label}</Td><Td data-testid={`tx-status-${p.payment_id}`}>{p.status_label}</Td><Td className="font-semibold">{rupiah(p.amount)}</Td>
            <Td>{p.commission != null ? rupiah(p.commission) : "-"}</Td><Td>{p.provider_net != null ? rupiah(p.provider_net) : "-"}</Td><Td>{p.payout_status || "-"}</Td>
          </tr>
        ))} />
      )}
    </div>
  );
}

export function AdminReviews() {
  const [f, setF] = useState({ rating: "", provider_id: "" });
  const [rows, setRows] = useState(null);
  const [providers, setProviders] = useState([]);
  const load = () => api.get("/admin/reviews", { params: clean(f) }).then((r) => setRows(r.data));
  useEffect(() => { load(); }, [f]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { api.get("/admin/users", { params: { role: "penyedia" } }).then((r) => setProviders(r.data)); }, []);
  const toggle = async (r) => {
    try { await api.post(`/admin/reviews/${r.review_id}/hidden`, { value: !r.hidden }); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="admin-reviews">
      <AdminHead title="Moderasi Ulasan" desc="Ulasan tersembunyi tidak tampil publik dan tidak dihitung dalam rata-rata rating." />
      <div className="mb-4 flex flex-wrap gap-2">
        <FilterSelect value={f.rating} onChange={(v) => setF({ ...f, rating: v })} testId="reviews-rating-filter" options={[["", "Semua bintang"], ...[5, 4, 3, 2, 1].map((n) => [String(n), `${n} bintang`])]} />
        <FilterSelect value={f.provider_id} onChange={(v) => setF({ ...f, provider_id: v })} testId="reviews-provider-filter" options={[["", "Semua penyedia"], ...providers.map((p) => [p.user_id, p.provider?.business_name || p.name])]} />
      </div>
      {rows === null ? <PageLoader /> : (
        <Table cols={["Penyedia", "Pengulas", "Rating", "Ulasan", "Tanggal", "Tampil"]} testId="reviews-table" rows={rows.map((r) => (
          <tr key={r.review_id} className={r.hidden ? "opacity-50" : ""} data-testid={`review-row-${r.review_id}`}>
            <Td>{r.provider_name}</Td><Td>{r.reviewer_name}</Td><Td><Stars value={r.rating} size="h-3.5 w-3.5" /></Td>
            <Td className="max-w-xs">{r.comment}</Td><Td>{formatDate(r.created_at)}</Td>
            <Td><Switch checked={!r.hidden} onCheckedChange={() => toggle(r)} data-testid={`review-visible-${r.review_id}`} /></Td>
          </tr>
        ))} />
      )}
    </div>
  );
}

const NUM = [["commission_percent", "Komisi (%)", "0–50"], ["credits_per_quote", "Kredit per penawaran", "≥ 0"], ["starter_credits", "Kredit awal penyedia baru", "≥ 0"],
  ["max_quotes_per_request", "Maks. penawaran per permintaan", "1–10"], ["request_expiry_days", "Kedaluwarsa permintaan (hari)", "1–90"]];

export function AdminSettings() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)); }, []);
  if (!s) return <PageLoader />;
  const setPkg = (i, k, v) => setS({ ...s, credit_packages: s.credit_packages.map((p, j) => (j === i ? { ...p, [k]: v } : p)) });
  const save = async (e) => {
    e.preventDefault();
    const payload = { ...s, credit_packages: s.credit_packages.map((p) => ({ ...p, credits: Number(p.credits), price: Number(p.price) })) };
    NUM.forEach(([k]) => { payload[k] = Number(payload[k]); });
    try { const { data } = await api.put("/admin/settings", payload); setS(data); toast.success("Pengaturan disimpan"); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <form onSubmit={save} data-testid="admin-settings">
      <AdminHead title="Pengaturan" desc="Perubahan hanya berlaku untuk aksi baru. Komisi pada transaksi lama tidak berubah." />
      <div className="card-soft grid gap-5 p-6 sm:grid-cols-2">
        {NUM.map(([k, label, hint]) => (
          <div key={k} className="space-y-1.5">
            <Label>{label} <span className="text-xs font-normal text-stone-400">({hint})</span></Label>
            <Input type="number" step={k === "commission_percent" ? "0.5" : "1"} value={s[k]} onChange={(e) => setS({ ...s, [k]: e.target.value })} className="rounded-xl" data-testid={`setting-${k}`} />
          </div>
        ))}
        <label className="flex items-center gap-3 text-sm sm:col-span-2">
          <Switch checked={s.require_provider_verification} onCheckedChange={(v) => setS({ ...s, require_provider_verification: v })} data-testid="setting-require_provider_verification" />
          Wajibkan verifikasi penyedia
        </label>
      </div>
      <div className="card-soft mt-6 space-y-3 p-6">
        <p className="font-bold text-stone-900">Paket kredit</p>
        {s.credit_packages.map((p, i) => (
          <div key={i} className="grid grid-cols-[1fr_1fr_1.4fr_auto] items-end gap-2" data-testid={`credit-pkg-row-${i}`}>
            <div className="space-y-1"><Label className="text-xs">Kode</Label><Input value={p.key} onChange={(e) => setPkg(i, "key", e.target.value)} className="rounded-lg" data-testid={`credit-pkg-key-${i}`} /></div>
            <div className="space-y-1"><Label className="text-xs">Kredit</Label><Input type="number" value={p.credits} onChange={(e) => setPkg(i, "credits", e.target.value)} className="rounded-lg" data-testid={`credit-pkg-credits-${i}`} /></div>
            <div className="space-y-1"><Label className="text-xs">Harga (Rp)</Label><Input type="number" value={p.price} onChange={(e) => setPkg(i, "price", e.target.value)} className="rounded-lg" data-testid={`credit-pkg-price-${i}`} /></div>
            <Button type="button" size="icon" variant="ghost" disabled={s.credit_packages.length === 1} onClick={() => setS({ ...s, credit_packages: s.credit_packages.filter((_, j) => j !== i) })} data-testid={`credit-pkg-remove-${i}`}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
          </div>
        ))}
        <Button type="button" variant="outline" size="sm" className="rounded-lg" data-testid="credit-pkg-add"
          onClick={() => setS({ ...s, credit_packages: [...s.credit_packages, { key: `k${Date.now() % 1000}`, credits: 5, price: 25000 }] })}><Plus className="mr-1 h-4 w-4" /> Tambah paket</Button>
      </div>
      <Button type="submit" className="mt-6 rounded-xl bg-teal-700 px-8 hover:bg-teal-800" data-testid="settings-save-button">Simpan Pengaturan</Button>
    </form>
  );
}
